import { createFileRoute, Link } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { AIRPORTS } from "@/lib/airports";
import { buildMeta } from "@/lib/seo";
import { useMemo } from "react";

export const Route = createFileRoute("/destinations")({
  head: () => {
    const base = buildMeta({
      title: "Kierunki lotów — wszystkie miasta i kraje | Tanie Latanie",
      description: "Przeglądaj setki kierunków lotów z Polski. Tanie bilety lotnicze do Europy, Azji, Ameryki i na egzotyczne wyspy.",
      path: "/destinations",
    });
    // ItemList obok BreadcrumbList — daje Google jasną listę kierunków
    // (kraje / wybrane miasta) z linkami do landingów krajowych. Limit do
    // pierwszych 50 wpisów, żeby payload pozostał rozsądny.
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Kierunki lotów z Polski",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: AIRPORTS.filter((a) => !a.isMetro && !a.isCountry)
        .slice(0, 50)
        .map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${a.city} (${a.code})`,
          url: `https://tanielatanie.pl/loty/${a.code.toLowerCase()}`,
        })),
    };
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Strona główna", item: "https://tanielatanie.pl/" },
              { "@type": "ListItem", position: 2, name: "Kierunki", item: "https://tanielatanie.pl/destinations" },
            ],
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(itemList) },
      ],
    };
  },
  component: DestinationsPage,
});

function DestinationsPage() {
  const { dd, rd } = useMemo(() => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun..6=Sat
    const daysToFri = ((5 - day + 7) % 7) || 7; // always next Fri (min 1 day ahead)
    const fri = new Date(now);
    fri.setDate(now.getDate() + daysToFri);
    const sun = new Date(fri);
    sun.setDate(fri.getDate() + 2);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { dd: iso(fri), rd: iso(sun) };
  }, []);

  const grouped = useMemo(() => {
    const m = new Map<string, typeof AIRPORTS>();
    for (const a of AIRPORTS) {
      const list = m.get(a.country) ?? [];
      list.push(a);
      m.set(a.country, list);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b, "pl"));
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <section className="container mx-auto px-4 py-12">
        <h1 className="font-display text-4xl font-bold md:text-5xl">Wszystkie kierunki</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Lataj do {AIRPORTS.length} lotnisk w {grouped.length} krajach. Kliknij dowolne miasto,
          aby sprawdzić ceny z Warszawy.
        </p>

        <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {grouped.map(([country, list]) => (
            <div key={country} className="glass rounded-lg p-5">
              <h2 className="font-display text-xl font-bold">{country}</h2>
              <ul className="mt-3 space-y-1">
                {list.map((a) => (
                  <li key={a.code} className="flex items-center justify-between">
                    <Link
                      to="/search"
                      search={{
                        ot: "RoundTrip", from: "WAR", to: a.code,
                        dd, rd,
                        a: 1, c: 0, i: 0, sc: "economy", d: 0,
                      } as never}
                      className="text-sm text-foreground underline-offset-4 transition hover:text-primary hover:underline"
                    >
                      {a.city} <span className="text-xs text-muted-foreground">— {a.name}</span>
                    </Link>
                    <span className="font-mono text-[10px] text-primary/80">{a.code}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
